import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { 
  fetchWordPressPosts, 
  fetchWordPressCategories, 
  transformWordPressPostToBlogPost,
  BlogPost
} from '../../services/wordpressApi';

const NewsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f9f7f4',
  padding: '0px 0 10px 0',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#455b40',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '16px',
  fontSize: '2.5rem',
  letterSpacing: '1px',
  textTransform: 'uppercase'
}));

const Divider = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '3px',
  backgroundColor: '#455b40',
  margin: '0 auto 60px',
}));

const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0,0,0,0.05)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(69, 91, 65, 0.15)',
    '& .blog-image': {
      transform: 'scale(1.05)',
    },
    '& .read-more-arrow': {
      transform: 'translateX(4px)',
    }
  }
}));

const BlogImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 240,
  overflow: 'hidden'
}));

const BlogImage = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.5s ease',
  position: 'relative'
}));

const DateOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: 0,
  backgroundColor: '#ffffff',
  color: '#455b41',
  padding: theme.spacing(1, 1.5),
  borderTopRightRadius: '8px',
  borderBottomRightRadius: '8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  zIndex: 2
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  color: '#455b41',
  fontWeight: 600,
  textTransform: 'none',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: 'rgba(69, 91, 65, 0.05)',
  }
}));

const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  color: '#455b41'
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: 'white',
  padding: '16px 40px',
  fontSize: '1rem',
  fontWeight: 700,
  borderRadius: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#5c7254',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(166, 144, 128, 0.4)'
  }
}));

// Helper function to parse Spanish date format
const parseSpanishDate = (dateStr: string): Date => {
  const months: { [key: string]: number } = {
    'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
    'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
    'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
  };
  
  // Handle format like "noviembre del 2020" or "15 de marzo del 2021"
  const cleanDate = dateStr.toLowerCase().replace('del ', '').replace('de ', '');
  const parts = cleanDate.split(' ');
  
  let day = 1;
  let month = 0;
  let year = new Date().getFullYear();
  
  if (parts.length === 2) {
    // Format: "noviembre 2020"
    const monthName = parts[0];
    year = parseInt(parts[1], 10);
    month = months[monthName] ?? 0;
  } else if (parts.length >= 3) {
    // Format: "15 marzo 2021"
    day = parseInt(parts[0], 10) || 1;
    const monthName = parts[1];
    year = parseInt(parts[parts.length - 1], 10);
    month = months[monthName] ?? 0;
  }
  
  return new Date(year, month, day);
};

const CTASection: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLatestPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [wpPosts, wpCategories] = await Promise.all([
          fetchWordPressPosts(),
          fetchWordPressCategories()
        ]);

        const transformedPosts = wpPosts.map(post => 
          transformWordPressPostToBlogPost(post, wpCategories)
        );

        // Sort posts by date (newest first)
        const sortedPosts = transformedPosts.sort((a, b) => {
          const dateA = parseSpanishDate(a.date);
          const dateB = parseSpanishDate(b.date);
          return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
        });

        // Get only the top 3 latest posts
        setPosts(sortedPosts.slice(0, 3));
      } catch (err) {
        setError('Error al cargar las publicaciones.');
        console.error('Error loading latest posts:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLatestPosts();
  }, []);

  const handleSeeAllNews = () => {
    navigate('/noticias-blog');
  };

  return (
    <NewsContainer>
      <Container maxWidth="lg">
        <SectionTitle>
        ÚLTIMAS NOTICIAS
        </SectionTitle>
        
        <Divider />

        {loading && (
          <LoadingSpinner>
            <CircularProgress size={60} sx={{ color: '#455b41' }} />
          </LoadingSpinner>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              md: '1fr 1fr', 
              lg: '1fr 1fr 1fr' 
            },
            gap: 4,
            mb: 6
          }}>
            {posts.map((post) => (
              <BlogCard key={post.id}>
                <BlogImageContainer>
                  <BlogImage
                    className="blog-image"
                    image={post.image}
                    title={post.title}
                  />
                  <DateOverlay>
                    {post.date}
                  </DateOverlay>
                </BlogImageContainer>
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  {/* Author and Category */}
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.8rem',
                      mb: 1.5,
                      display: 'block',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    By{' '}
                    <span style={{ color: '#455b41', fontWeight: 700 }}>
                      {post.author}
                    </span>
                    {post.category !== 'General' && post.category !== 'Uncategorized' && (
                      <>
                        {' | '}
                        <span style={{ color: '#455b41', fontWeight: 600 }}>
                          {post.category}
                        </span>
                      </>
                    )}
                  </Typography>
                  
                  {/* Title */}
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: '#2a3a28',
                      fontSize: '1.2rem',
                      lineHeight: 1.3,
                      mb: 1.5,
                      fontFamily: '"Lora", serif',
                      cursor: 'pointer',
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#455b41'
                      }
                    }}
                    onClick={() => navigate(`/${post.slug}`)}
                  >
                    {post.title}
                  </Typography>
                  
                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2.5,
                      lineHeight: 1.6,
                      fontSize: '0.9rem'
                    }}
                  >
                    {post.excerpt}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <ReadMoreButton
                    endIcon={<ArrowForwardIcon className="read-more-arrow" sx={{ transition: 'transform 0.3s ease' }} />}
                    onClick={() => navigate(`/${post.slug}`)}
                  >
                    Más detalles →
                  </ReadMoreButton>
                </CardActions>
              </BlogCard>
            ))}
          </Box>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <CTAButton onClick={handleSeeAllNews}>
            VER TODAS LAS NOTICIAS
          </CTAButton>
        </Box>
      </Container>
    </NewsContainer>
  );
};

export default CTASection;